import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  Descriptions,
  Tag,
  Space,
  Button,
  Form,
  Input,
  InputNumber,
  Switch,
  Select,
  Empty,
  Spin,
  Row,
  Col,
  Timeline,
  Alert,
  Typography,
  Popconfirm,
} from "antd";
import { ScheduleOutlined, ArrowLeftOutlined, SaveOutlined, EditOutlined } from "@ant-design/icons";
import PageHeader from "./common/PageHeader";
import { notify, serverError } from "../utils/notify";
import { useAuth } from "../context/AuthContext";
import { getVisit, updateVisit, transitionVisit } from "../network/api";
import { relativeTime } from "../constants/health";
import {
  VISIT_TYPE,
  VISIT_TYPE_LABEL,
  VISIT_TYPE_TAG_COLOR,
  VISIT_STATUS,
  VISIT_STATUS_LABEL,
  VISIT_STATUS_TAG_COLOR,
  availableVisitActions,
} from "../constants/visits";
import InstallActivation from "./InstallActivation";
import SupervisorReview from "./SupervisorReview";
import PhotoCapture from "./PhotoCapture";
import GeoCapture from "./GeoCapture";
import VisitReassign from "./VisitReassign";
import AddVisit from "./Modals/addVisit";
import dayjs from "dayjs";

const { Text, Paragraph } = Typography;

export default function VisitDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isHardwareAdmin } = useAuth();
  const [form] = Form.useForm();

  const [visit, setVisit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [saving, setSaving] = useState(false);
  const [acting, setActing] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [offline, setOffline] = useState(typeof navigator !== "undefined" && !navigator.onLine);
  const [draft, setDraft] = useState(null); // unsynced local draft, if any
  const draftKey = `visit-draft-${id}`;

  const fetchVisit = useCallback(async () => {
    setLoading(true);
    try {
      const v = await getVisit(id);
      setVisit(v);
      // Seed checklist form from saved answers.
      const answers = {};
      (v.checklistAnswers || []).forEach((a) => (answers[a.key] = a.value));
      form.setFieldsValue({ notes: v.notes, outcome: v.outcome, ...answers });
      // Offer to restore a local draft saved on this device (e.g. while offline).
      const raw = localStorage.getItem(`visit-draft-${id}`);
      setDraft(raw ? JSON.parse(raw) : null);
    } catch (error) {
      if (error?.response?.status === 404) setNotFound(true);
      else notify.error(serverError(error, "Failed to load visit"));
    } finally {
      setLoading(false);
    }
  }, [id, form]);

  useEffect(() => {
    if (id) fetchVisit();
  }, [id, fetchVisit]);

  useEffect(() => {
    const on = () => setOffline(false);
    const off = () => setOffline(true);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  if (loading) return <div style={{ padding: 60, textAlign: "center" }}><Spin size="large" /></div>;
  if (notFound || !visit) {
    return (
      <div>
        <Button type="link" icon={<ArrowLeftOutlined />} onClick={() => navigate("/dashboard/visits")} style={{ paddingLeft: 0, marginBottom: 12 }}>
          Back to Visits
        </Button>
        <Empty description="Visit not found" />
      </div>
    );
  }

  const isOwner = visit.technician?._id === user?._id;
  const canEdit =
    (isOwner || isHardwareAdmin) &&
    [VISIT_STATUS.SCHEDULED, VISIT_STATUS.IN_PROGRESS].includes(visit.status);
  const items = visit.checklistTemplate?.items || [];
  const actions = isOwner || isHardwareAdmin ? availableVisitActions(visit.status) : [];

  const onSaveChecklist = async () => {
    setSaving(true);
    try {
      const values = form.getFieldsValue();
      const checklistAnswers = items.map((it) => ({
        key: it.key,
        label: it.label,
        type: it.type,
        value: values[it.key],
      }));
      await updateVisit(id, { checklistAnswers, notes: values.notes, outcome: values.outcome });
      // Only reflect the saved fields locally — the PUT response is unpopulated, so
      // merging it whole would clobber populated shop/technician/checklistTemplate.
      setVisit((v) => ({ ...v, checklistAnswers, notes: values.notes, outcome: values.outcome }));
      localStorage.removeItem(draftKey); // synced — drop the local draft
      setDraft(null);
      notify.success("Progress saved");
    } catch (error) {
      notify.error(serverError(error, "Failed to save"));
    } finally {
      setSaving(false);
    }
  };

  // Persist the in-progress checklist to this device on every change, so nothing is
  // lost if the technician goes offline or the tab closes before a server save.
  const saveDraft = () => {
    localStorage.setItem(draftKey, JSON.stringify({ values: form.getFieldsValue(), savedAt: Date.now() }));
  };
  const restoreDraft = () => {
    if (draft?.values) {
      form.setFieldsValue(draft.values);
      notify.success("Draft restored");
    }
    setDraft(null);
  };
  const discardDraft = () => {
    localStorage.removeItem(draftKey);
    setDraft(null);
  };

  const onAction = async (action) => {
    setActing(true);
    try {
      // Persist checklist before completing so nothing is lost.
      if (action === "complete" && canEdit) await onSaveChecklist();
      await transitionVisit(id, action);
      notify.success(
        action === "complete" ? "Visit completed" : action === "start" ? "Visit started" : "Visit cancelled"
      );
      fetchVisit();
    } catch (error) {
      notify.error(serverError(error, `Failed to ${action} visit`));
    } finally {
      setActing(false);
    }
  };

  const renderField = (it) => {
    switch (it.type) {
      case "boolean":
        return (
          <Form.Item key={it.key} name={it.key} label={it.label} valuePropName="checked">
            <Switch disabled={!canEdit} />
          </Form.Item>
        );
      case "number":
        return (
          <Form.Item key={it.key} name={it.key} label={it.label} rules={it.required ? [{ required: true }] : []}>
            <InputNumber style={{ width: "100%" }} disabled={!canEdit} />
          </Form.Item>
        );
      case "select":
        return (
          <Form.Item key={it.key} name={it.key} label={it.label} rules={it.required ? [{ required: true }] : []}>
            <Select disabled={!canEdit} options={(it.options || []).map((o) => ({ value: o, label: o }))} allowClear />
          </Form.Item>
        );
      case "photo":
        return (
          <Form.Item key={it.key} name={it.key} label={`${it.label} (photo URL)`}>
            <Input disabled={!canEdit} placeholder="https://…" />
          </Form.Item>
        );
      default:
        return (
          <Form.Item key={it.key} name={it.key} label={it.label} rules={it.required ? [{ required: true }] : []}>
            <Input disabled={!canEdit} />
          </Form.Item>
        );
    }
  };

  const infoItems = [
    { key: "type", label: "Type", children: <Tag color={VISIT_TYPE_TAG_COLOR[visit.visitType]}>{VISIT_TYPE_LABEL[visit.visitType]}</Tag> },
    { key: "status", label: "Status", children: <Tag color={VISIT_STATUS_TAG_COLOR[visit.status]}>{VISIT_STATUS_LABEL[visit.status]}</Tag> },
    { key: "outlet", label: "Outlet", children: visit.shop ? `${visit.shop.code} — ${visit.shop.title}` : visit.outletCode || "—" },
    { key: "device", label: "Device", children: visit.deviceId || "—" },
    { key: "tech", label: "Technician", children: isHardwareAdmin && visit.status !== VISIT_STATUS.CANCELLED ? <VisitReassign visit={visit} onChange={fetchVisit} /> : (visit.technician?.name || "Unassigned") },
    ...(visit.ticket ? [{ key: "ticket", label: "Linked ticket", children: <Button type="link" style={{ padding: 0 }} onClick={() => navigate(`/dashboard/tickets/${visit.ticket._id}`)}>{visit.ticket.title}</Button> }] : []),
    { key: "sched", label: "Scheduled", children: visit.scheduledAt ? dayjs(visit.scheduledAt).format("YYYY-MM-DD HH:mm") : "—" },
    { key: "created", label: "Created", children: visit.createdAt ? `${dayjs(visit.createdAt).format("YYYY-MM-DD HH:mm")} (${relativeTime(visit.createdAt)})` : "—" },
    { key: "geo", label: "Location", children: <GeoCapture visit={visit} canEdit={canEdit} onChange={fetchVisit} /> },
  ];

  return (
    <div>
      <PageHeader
        title="Visit"
        subtitle={`${VISIT_TYPE_LABEL[visit.visitType]} · ${visit.shop?.code || visit.deviceId || ""}`}
        icon={<ScheduleOutlined />}
        extra={
          <Space>
            {actions.map((a) =>
              a.key === "cancel" ? (
                <Popconfirm key={a.key} title="Cancel this visit?" okText="Yes, cancel" okButtonProps={{ danger: true }} onConfirm={() => onAction(a.key)}>
                  <Button danger loading={acting}>{a.label}</Button>
                </Popconfirm>
              ) : (
                <Button key={a.key} type={a.type} loading={acting} onClick={() => onAction(a.key)}>{a.label}</Button>
              )
            )}
            {isHardwareAdmin && visit.status !== VISIT_STATUS.CANCELLED && (
              <Button icon={<EditOutlined />} onClick={() => setEditOpen(true)}>Edit</Button>
            )}
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/dashboard/visits")}>Back</Button>
          </Space>
        }
      />

      {isHardwareAdmin && (
        <AddVisit
          visible={editOpen}
          visit={visit}
          onClose={() => setEditOpen(false)}
          onSubmit={() => { setEditOpen(false); fetchVisit(); }}
        />
      )}

      {offline && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="You're offline"
          description="Your checklist is being saved on this device. Tap “Save progress” once you're back online to sync it."
        />
      )}
      {draft && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="Unsaved draft found on this device"
          description={draft.savedAt ? `Last edited ${relativeTime(new Date(draft.savedAt))}.` : undefined}
          action={
            <Space>
              <Button size="small" type="primary" onClick={restoreDraft}>Restore</Button>
              <Button size="small" onClick={discardDraft}>Discard</Button>
            </Space>
          }
        />
      )}

      <Form form={form} layout="vertical" onValuesChange={canEdit ? saveDraft : undefined}>
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={10}>
            <Card title="Details" style={{ height: "100%" }}>
              <Descriptions column={1} size="small" items={infoItems} />
              {visit.notes && !canEdit && (
                <Paragraph type="secondary" style={{ marginTop: 12 }}>
                  <Text strong>Notes: </Text>{visit.notes}
                </Paragraph>
              )}
            </Card>
          </Col>

          <Col xs={24} lg={14}>
            <Card
              title={visit.checklistTemplate ? `Checklist — ${visit.checklistTemplate.name}` : "Checklist"}
              extra={canEdit && items.length > 0 && (
                <Button icon={<SaveOutlined />} loading={saving} onClick={onSaveChecklist}>Save progress</Button>
              )}
            >
              {items.length === 0 ? (
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No checklist attached to this visit" />
              ) : (
                items.map(renderField)
              )}
              {canEdit && (
                <>
                  <Form.Item name="outcome" label="Outcome">
                    <Input.TextArea rows={2} placeholder="Summary of work done" />
                  </Form.Item>
                  <Form.Item name="notes" label="Notes">
                    <Input.TextArea rows={2} />
                  </Form.Item>
                </>
              )}
            </Card>
          </Col>

          {visit.visitType === VISIT_TYPE.INSTALL && (
            <Col span={24}>
              <InstallActivation visit={visit} canEdit={canEdit} onChange={fetchVisit} />
            </Col>
          )}

          {visit.status !== VISIT_STATUS.CANCELLED && (
            <Col span={24}>
              <PhotoCapture visit={visit} canEdit={canEdit} onChange={fetchVisit} />
            </Col>
          )}

          {visit.status === VISIT_STATUS.COMPLETED && (
            <Col span={24}>
              <SupervisorReview visit={visit} onChange={fetchVisit} />
            </Col>
          )}

          {visit.history?.length > 0 && (
            <Col span={24}>
              <Card title="History">
                <Timeline
                  items={visit.history.map((h) => ({
                    children: (
                      <span>
                        <Text strong>{VISIT_STATUS_LABEL[h.to] || h.to}</Text>
                        {h.byName ? <Text type="secondary"> · {h.byName}</Text> : null}
                        {h.note ? <div><Text type="secondary">{h.note}</Text></div> : null}
                        <div><Text type="secondary" style={{ fontSize: 12 }}>{relativeTime(h.at)}</Text></div>
                      </span>
                    ),
                  }))}
                />
              </Card>
            </Col>
          )}
        </Row>
      </Form>
    </div>
  );
}
